import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import { pantryApi, affiliateApi, peopleApi } from "../services/api";
import BottomSheet from "../components/shared/BottomSheet";
import SavingNotice from "../components/shared/SavingNotice";
import { API_BASE } from "../utils/apiBase";

const BASE_URL = API_BASE;
const TRIP_CONTEXT_MARKER = "--- HouseMind trip context ---";
const TRIP_CONTEXT_FIELDS = [
  ["origin", "Origin"],
  ["lodgingArea", "Lodging area"],
  ["transportMode", "Transportation"],
  ["flightNumber", "Flight number"],
  ["airline", "Airline"],
  ["budget", "Budget"],
  ["pace", "Pace"],
  ["mustDo", "Must do"],
  ["avoid", "Avoid"],
  ["dietary", "Dietary needs"],
  ["mobility", "Mobility/accessibility"],
];

const emptyTripContext = {
  origin: "",
  lodgingArea: "",
  transportMode: "",
  flightNumber: "",
  airline: "",
  budget: "",
  pace: "",
  mustDo: "",
  avoid: "",
  dietary: "",
  mobility: "",
};

function parseLocalDate(value) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysBetween(start, end) {
  if (!start || !end) return null;
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

function tripTransportSignals(tripDetails = {}) {
  const transport = [
    tripDetails.transportMode,
    tripDetails.tripStyle,
    tripDetails.notes,
    tripDetails.mustDo,
    tripDetails.avoid,
    tripDetails.flightNumber,
    tripDetails.airline,
  ].join(" ").toLowerCase();
  const isRentalCar = /\b(rental|renting|rental car|car rental)\b/.test(transport);
  const isRoadTrip = /\b(car|cars|road|drive|driving|vehicle|van|rv|highway)\b/.test(transport) && !isRentalCar;
  const hasAirTravel = /\b(flight|fly|flying|airport|plane|airline|boarding|tsa)\b/.test(transport);
  return { hasAirTravel, isRentalCar, isRoadTrip };
}

function buildTripPrepItems({ tripDetails = {}, pantryExpiring = [], travelDocuments = null, templatePacking = null }) {
  const startDate = parseLocalDate(tripDetails.startDate);
  const endDate = parseLocalDate(tripDetails.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = startDate ? daysBetween(today, startDate) : null;
  const duration = Number(tripDetails.duration || 0);
  const { hasAirTravel, isRentalCar, isRoadTrip } = tripTransportSignals(tripDetails);
  const isLongTrip = duration >= 5 || (startDate && endDate && daysBetween(startDate, endDate) >= 5);
  const items = [];

  const packingTotal = templatePacking?.total_items || 0;
  const packingChecked = templatePacking?.checked_items || 0;
  if (!packingTotal) {
    items.push({
      label: "Build starter packing checklist",
      detail: "Use saved trip dates, destination, travelers, and trip style to create essentials without an AI call.",
      action: "Packing",
      target: "packing",
      tone: "soon",
    });
  } else if (packingChecked < packingTotal) {
    items.push({
      label: "Finish packing checklist",
      detail: `${packingTotal - packingChecked} of ${packingTotal} starter items still open.`,
      action: "Packing",
      target: "packing",
      tone: "soon",
    });
  }

  if (pantryExpiring.length > 0) {
    items.push({
      label: "Use or freeze expiring food",
      detail: `${pantryExpiring.slice(0, 3).map((item) => item.name).join(", ")}${pantryExpiring.length > 3 ? ` and ${pantryExpiring.length - 3} more` : ""} need attention before or during this trip.`,
      action: "Food",
      target: "food",
      tone: "urgent",
    });
  }

  const docStatus = travelDocuments?.status;
  const docItems = travelDocuments?.items || [];
  const requiredDocs = travelDocuments?.required_documents || [];
  if (!travelDocuments) {
    items.push({
      label: "Check travel documents",
      detail: "Compare the trip against saved Home Vault IDs, passport dates, and static destination rules.",
      action: "Docs",
      target: "vault",
      tone: "soon",
    });
  } else if (docStatus && docStatus !== "ready") {
    items.push({
      label: docStatus === "urgent" ? "Fix travel document issue" : "Review travel documents",
      detail: docItems[0]?.message || `${requiredDocs.join(", ") || "Saved documents"} need a quick review.`,
      action: "Docs",
      target: "vault",
      tone: docStatus === "urgent" ? "urgent" : "soon",
    });
  }

  if (!tripDetails.startDate || !tripDetails.origin || !tripDetails.lodgingArea) {
    const missing = [
      !tripDetails.startDate ? "dates" : null,
      !tripDetails.origin ? "leaving from" : null,
      !tripDetails.lodgingArea ? "staying near" : null,
    ].filter(Boolean);
    items.push({
      label: "Complete trip basics",
      detail: `Add ${missing.join(", ")} so HouseMind can time reminders and prep checks correctly.`,
      action: "Details",
      target: "details",
      tone: "soon",
    });
  }

  if (hasAirTravel) {
    items.push({
      label: daysUntil !== null && daysUntil <= 1 ? "Check in for flight" : daysUntil !== null && daysUntil <= 7 ? "Reserve airport parking or ride" : "Plan airport transfer",
      detail: daysUntil !== null && daysUntil <= 1
        ? "Check in, confirm seats, boarding passes, bags, and ride timing."
        : "Confirm airport parking, rideshare, drop-off, baggage rules, and arrival time.",
      tone: daysUntil !== null && daysUntil <= 1 ? "urgent" : "soon",
    });
  }

  if (isRoadTrip) {
    items.push({
      label: "Check vehicle readiness",
      detail: "Review fuel, tire pressure, service status, registration, insurance, chargers, and roadside kit.",
      action: "Vehicles",
      target: "vehicles",
      tone: "soon",
    });
  }

  if (isRentalCar) {
    items.push({
      label: "Confirm rental car pickup",
      detail: "Check pickup time, license, insurance coverage, deposit card, car seat needs, tolls, and return fuel rules.",
      tone: "soon",
    });
  }

  items.push({
    label: "Set home departure basics",
    detail: "Trash, thermostat, lights, doors, water, mail/packages, and anything that should be off while you are away.",
    tone: "soon",
  });

  if (isLongTrip) {
    items.push({
      label: "Review pauseable subscriptions",
      detail: "Meal kits, gym, local services, and deliveries may be worth pausing while you are away.",
      action: "Subscriptions",
      target: "subscriptions",
      tone: "optional",
    });
  }

  if (tripDetails.dietary || tripDetails.mobility) {
    items.push({
      label: "Match plans to traveler needs",
      detail: "Keep food, accessibility, pace, and comfort notes visible when saving activities or restaurants.",
      tone: "optional",
    });
  }

  return items;
}

function buildFallbackTripLinks(destination = "", tripStyle = "") {
  const q = encodeURIComponent(destination || "travel");
  const style = `${tripStyle || ""}`.toLowerCase();
  const links = {
    booking_com: `https://www.booking.com/searchresults.html?ss=${q}&utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    hotels_com: `https://www.hotels.com/Hotel-Search?destination=${q}&utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    airbnb: `https://www.airbnb.com/s/${q}/homes?utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    viator: `https://www.viator.com/searchResults/all?text=${q}&utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    getyourguide: `https://www.getyourguide.com/s/?q=${q}&utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    skyscanner: `https://www.skyscanner.com/transport/flights/any/${q}/?utm_source=housemind&utm_medium=app&utm_campaign=trip_planning`,
    allianz: `https://www.allianztravelinsurance.com/travel/insurance-plans.htm?utm_source=housemind&utm_medium=app&utm_campaign=travel_insurance`,
    world_nomads: `https://www.worldnomads.com/travel-insurance?destination=${q}&utm_source=housemind&utm_medium=app&utm_campaign=travel_insurance`,
  };
  if (style.includes("road") || style.includes("camp") || style.includes("ski") || style.includes("adventure")) {
    links.rei = `https://www.rei.com/search?q=${encodeURIComponent(`${destination || ""} travel gear`.trim() || "travel gear")}&utm_source=housemind&utm_medium=app&utm_campaign=outdoor_gear`;
  }
  return links;
}

async function parseTripResponse(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {
      error: res.ok
        ? "Trip response could not be read. Please try again."
        : `Trip service returned an unreadable response (${res.status}). Please try again.`,
    };
  }
}

async function tripRequest(path, body, token) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await parseTripResponse(res);
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

async function tripGet(path, token) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "X-Requested-With": "XMLHttpRequest", Authorization: `Bearer ${token}` },
  });
  const data = await parseTripResponse(res);
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

async function tripPatch(path, body, token) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await parseTripResponse(res);
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

async function apiPatch(path, body, token) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await parseTripResponse(res);
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

function DetailPill({ label, value }) {
  if (!value) return null;
  return (
    <div className="travel-pill">
      <span className="travel-pill-label">{label}</span>
      <span className="travel-pill-value">{value}</span>
    </div>
  );
}

function TravelStatCard({ label, value, hint }) {
  return (
    <div className="travel-stat-card">
      <div className="travel-stat-label">{label}</div>
      <div className="travel-stat-value">{value}</div>
      {hint && <div className="travel-stat-hint">{hint}</div>}
    </div>
  );
}

function TravelSection({ eyebrow, title, subtitle, children }) {
  return (
    <section className="travel-section">
      <div className="travel-section-header">
        {eyebrow && <div className="travel-section-eyebrow">{eyebrow}</div>}
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function TripPrepCard({ tripDetails, prepItems, onOpenTarget }) {
  const startDate = parseLocalDate(tripDetails.startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = startDate ? daysBetween(today, startDate) : null;

  return (
    <div className="card mb-24 trip-prep-card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="flex-between" style={{ gap: 12, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
            Trip prep
          </div>
          <h3 style={{ margin: 0, fontSize: 20 }}>✅ Before you leave</h3>
        </div>
        {daysUntil !== null && (
          <span className="badge" style={{ background: "var(--green-light)", color: "var(--green)" }}>
            {daysUntil < 0 ? "In progress" : daysUntil === 0 ? "Today" : `${daysUntil}d`}
          </span>
        )}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {prepItems.map((item) => (
          <div key={item.label} className={`travel-packing-item trip-prep-item is-${item.tone || "soon"}`} style={{ cursor: "default" }}>
            <div style={{
              width: 20, height: 20, borderRadius: 4, border: "1.5px solid var(--border-strong)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
            }}>
              <span style={{ color: "var(--green)", fontSize: 12 }}>•</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{item.label}</div>
              <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>{item.detail}</div>
            </div>
            {item.action && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onOpenTarget?.(item.target)}
                style={{ flexShrink: 0 }}
              >
                {item.action}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function splitTripNotes(rawNotes = "") {
  const text = rawNotes || "";
  const markerIndex = text.indexOf(TRIP_CONTEXT_MARKER);
  if (markerIndex === -1) {
    return { notes: text, context: { ...emptyTripContext } };
  }

  const notes = text.slice(0, markerIndex).trim();
  const contextText = text.slice(markerIndex + TRIP_CONTEXT_MARKER.length);
  const context = { ...emptyTripContext };
  contextText.split("\n").forEach((line) => {
    const [rawLabel, ...rest] = line.split(":");
    if (!rawLabel || rest.length === 0) return;
    const label = rawLabel.trim().toLowerCase();
    const value = rest.join(":").trim();
    const match = TRIP_CONTEXT_FIELDS.find(([, fieldLabel]) => fieldLabel.toLowerCase() === label);
    if (match) context[match[0]] = value;
  });
  return { notes, context };
}

function composeTripNotes(notes = "", context = emptyTripContext) {
  const contextLines = TRIP_CONTEXT_FIELDS
    .map(([key, label]) => {
      const value = String(context?.[key] || "").trim();
      return value ? `${label}: ${value}` : "";
    })
    .filter(Boolean);
  const cleanNotes = String(notes || "").trim();
  if (!contextLines.length) return cleanNotes;
  return [cleanNotes, TRIP_CONTEXT_MARKER, ...contextLines].filter(Boolean).join("\n");
}

function normalizePackingItem(entry, fallbackCategory = "") {
  if (!entry) return null;
  if (typeof entry === "string") {
    const item = entry.trim();
    return item ? { category: fallbackCategory, item } : null;
  }

  const item = String(entry.item || "").trim();
  if (!item) return null;

  return {
    ...entry,
    category: String(entry.category || fallbackCategory || "").trim(),
    item,
  };
}

function samePackingItem(left, right) {
  const a = normalizePackingItem(left);
  const b = normalizePackingItem(right);
  if (!a || !b) return false;
  if (a.item.toLowerCase() !== b.item.toLowerCase()) return false;
  if (!a.category || !b.category) return true;
  return a.category.toLowerCase() === b.category.toLowerCase();
}

function normalizeSavedPackingItems(items = []) {
  const normalized = [];
  (items || []).forEach((entry) => {
    const item = normalizePackingItem(entry);
    if (item && !normalized.some((existing) => samePackingItem(existing, item))) {
      normalized.push(item);
    }
  });
  return normalized;
}

function buildDayPlanDraft(days = []) {
  return (days || []).map((day) => ({
    day: day.day,
    theme: day.theme || "",
    pace: day.pace || "",
    highlights: (day.highlights || []).join("\n"),
    notes: day.notes || "",
  }));
}

function getPackingCategoryIcon(category = "") {
  const key = category.toLowerCase();
  if (key.includes("cloth")) return "👕";
  if (key.includes("beach") || key.includes("swim")) return "🏖️";
  if (key.includes("toilet") || key.includes("bath")) return "🧴";
  if (key.includes("document") || key.includes("id")) return "🪪";
  if (key.includes("tech") || key.includes("charger")) return "🔌";
  if (key.includes("health") || key.includes("med")) return "💊";
  if (key.includes("food") || key.includes("snack")) return "🥨";
  return "🎒";
}

function PackingCategory({ category, items, savedItemKeys = [], onToggleSave = null, disabled = false, tripStyle = "" }) {
  const packedCount = (items || []).filter((item) => (
    savedItemKeys.includes(`${category}::${item.item}`.toLowerCase()) || savedItemKeys.includes(`::${item.item}`.toLowerCase())
  )).length;

  return (
    <div className="travel-packing-category">
      <div className="travel-packing-header">
        <span>{getPackingCategoryIcon(category)}</span>
        <strong>{category}</strong>
        <em>{packedCount} of {items.length} packed</em>
      </div>
      <div className="travel-packing-items">
        {items.map((item, i) => (
          <PackingItem
            key={i}
            item={item}
            category={category}
            isSaved={savedItemKeys.includes(`${category}::${item.item}`.toLowerCase()) || savedItemKeys.includes(`::${item.item}`.toLowerCase())}
            onToggleSave={onToggleSave}
            disabled={disabled}
            tripStyle={tripStyle}
          />
        ))}
      </div>
    </div>
  );
}

function EditablePackingCategory({ category, items, onCategoryChange, onItemChange }) {
  return (
    <div className="travel-packing-category">
      <input
        className="form-input"
        value={category}
        onChange={(e) => onCategoryChange(e.target.value)}
        style={{ marginBottom: 10, fontWeight: 600 }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item, i) => (
          <div key={i} className="card card-sm" style={{ display: "grid", gap: 8 }}>
            <input
              className="form-input"
              value={item.item || ""}
              onChange={(e) => onItemChange(i, "item", e.target.value)}
              placeholder="Item"
            />
            <div className="grid-2">
              <input
                className="form-input"
                value={item.quantity || ""}
                onChange={(e) => onItemChange(i, "quantity", e.target.value)}
                placeholder="Quantity"
              />
              <input
                className="form-input"
                value={item.note || ""}
                onChange={(e) => onItemChange(i, "note", e.target.value)}
                placeholder="Optional note"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PackingItemBuyLink({ itemName, tripStyle }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFetch = async (e) => {
    e.stopPropagation();
    if (url) {
      affiliateApi.track("amazon", "trips", url).catch(() => {});
      window.open(url, "_blank", "noopener noreferrer");
      return;
    }
    setLoading(true);
    try {
      const data = await affiliateApi.packingItemLink(itemName, tripStyle);
      if (data?.url) {
        setUrl(data.url);
        affiliateApi.track("amazon", "trips", data.url).catch(() => {});
        window.open(data.url, "_blank", "noopener noreferrer");
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleFetch}
      disabled={loading}
      style={{
        fontSize: 11, color: "var(--ink-hint)", background: "none",
        border: "none", cursor: "pointer", padding: "1px 4px",
        opacity: loading ? 0.5 : 1, whiteSpace: "nowrap",
        flexShrink: 0, alignSelf: "center",
      }}
      title="Search on Amazon"
    >
      {loading ? "…" : "Buy ↗"}
    </button>
  );
}

function PackingItem({ item, category, isSaved = false, onToggleSave = null, disabled = false, tripStyle = "" }) {
  const checked = isSaved;

  const handleToggle = () => {
    if (!onToggleSave || disabled) return;
    onToggleSave({ category, ...item });
  };

  return (
    <div
      className={`travel-packing-item${checked ? " is-checked" : ""}${disabled ? " is-disabled" : ""}`}
      role={onToggleSave ? "button" : undefined}
      tabIndex={onToggleSave && !disabled ? 0 : undefined}
      onClick={handleToggle}
      onKeyDown={(event) => {
        if ((event.key === "Enter" || event.key === " ") && onToggleSave && !disabled) {
          event.preventDefault();
          handleToggle();
        }
      }}
      style={{ cursor: onToggleSave && !disabled ? "pointer" : "default" }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: 4, border: "1.5px solid",
        borderColor: checked ? "var(--green)" : "var(--border-strong)",
        background: checked ? "var(--green)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, marginTop: 1, transition: "all 0.15s",
        pointerEvents: "none",
      }}>
        {checked && <span style={{ color: "white", fontSize: 12 }}>✓</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 14, fontWeight: 500,
          textDecoration: checked ? "line-through" : "none",
          color: checked ? "var(--ink-muted)" : "var(--ink)",
        }}>
          {item.quantity && item.quantity > 1 ? `${item.item} ×${item.quantity}` : item.item}
        </div>
        {item.note && (
          <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>{item.note}</div>
        )}
        {item.why && (
          <div style={{ fontSize: 12, color: "var(--green)", marginTop: 2 }}>{item.why}</div>
        )}
      </div>
      {isSaved ? (
        <span
          className="badge"
          style={{ alignSelf: "center", background: "var(--green-light)", color: "var(--green)" }}
        >
          Saved
        </span>
      ) : (
        <PackingItemBuyLink itemName={item.item} tripStyle={tripStyle} />
      )}
    </div>
  );
}

// ── Trip Affiliate Section ────────────────────────────────────────────────────

function TripAffiliateSection({ destination, tripStyle, checkin, checkout, guests }) {
  const [links, setLinks] = useState(() => destination ? buildFallbackTripLinks(destination, tripStyle) : null);

  useEffect(() => {
    if (!destination) {
      setLinks(null);
      return;
    }
    setLinks(buildFallbackTripLinks(destination, tripStyle));
    affiliateApi.tripLinks({
      destination,
      trip_style: tripStyle || "",
      checkin: checkin || "",
      checkout: checkout || "",
      guests: guests || 1,
    }).then((data) => {
      setLinks({ ...buildFallbackTripLinks(destination, tripStyle), ...(data || {}) });
    }).catch(() => {});
  }, [destination, tripStyle, checkin, checkout, guests]);

  if (!links) return null;

  const hotelOptions = [
    { key: "booking_com",   label: "Booking.com",  url: links.booking_com,  icon: "🏨" },
    { key: "hotels_com",    label: "Hotels.com",   url: links.hotels_com,   icon: "🏩" },
    { key: "airbnb",        label: "Airbnb",       url: links.airbnb,       icon: "🏠" },
  ].filter(o => o.url);

  const activityOptions = [
    { key: "viator",        label: "Viator",       url: links.viator,       icon: "🎟️" },
    { key: "getyourguide",  label: "GetYourGuide", url: links.getyourguide, icon: "🗺️" },
  ].filter(o => o.url);

  const flightOptions = [
    { key: "skyscanner",    label: "Skyscanner",   url: links.skyscanner,   icon: "✈️" },
  ].filter(o => o.url);

  const insuranceOptions = [
    { key: "allianz",       label: "Allianz Travel", url: links.allianz,    icon: "🛡️" },
    { key: "world_nomads",  label: "World Nomads",   url: links.world_nomads, icon: "🌍" },
  ].filter(o => o.url);

  const gearOptions = [
    { key: "rei", label: "Travel gear", url: links.rei, icon: "🎒" },
  ].filter(o => o.url);

  const hasAny = [hotelOptions, activityOptions, flightOptions, insuranceOptions, gearOptions].some(g => g.length > 0);
  if (!hasAny) return null;

  return (
    <div className="fd-shopping-section travel-affiliate-card">
      <div className="fd-shopping-header travel-affiliate-header">
        <h3>✈️ Book trip basics</h3>
      </div>

      {hotelOptions.length > 0 && (
        <div className="travel-affiliate-group">
          <div className="travel-affiliate-group-title">
            🏨 Accommodation
          </div>
          <div className="travel-affiliate-grid">
            {hotelOptions.map(opt => (
              <a key={opt.key} href={opt.url} target="_blank" rel="noopener noreferrer"
                className="affiliate-btn affiliate-food travel-affiliate-link"
                onClick={() => affiliateApi.track(opt.key, "trips", opt.url).catch(() => {})}
              >
                <span>{opt.icon}</span><span>{opt.label}</span>
                <span className="travel-affiliate-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {activityOptions.length > 0 && (
        <div className="travel-affiliate-group">
          <div className="travel-affiliate-group-title">
            🎟️ Activities & tours
          </div>
          <div className="travel-affiliate-grid">
            {activityOptions.map(opt => (
              <a key={opt.key} href={opt.url} target="_blank" rel="noopener noreferrer"
                className="affiliate-btn affiliate-food travel-affiliate-link"
                onClick={() => affiliateApi.track(opt.key, "trips", opt.url).catch(() => {})}
              >
                <span>{opt.icon}</span><span>{opt.label}</span>
                <span className="travel-affiliate-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {flightOptions.length > 0 && (
        <div className="travel-affiliate-group">
          <div className="travel-affiliate-group-title">
            ✈️ Flights
          </div>
          <div className="travel-affiliate-grid">
            {flightOptions.map(opt => (
              <a key={opt.key} href={opt.url} target="_blank" rel="noopener noreferrer"
                className="affiliate-btn affiliate-food travel-affiliate-link"
                onClick={() => affiliateApi.track(opt.key, "trips", opt.url).catch(() => {})}
              >
                <span>{opt.icon}</span><span>{opt.label}</span>
                <span className="travel-affiliate-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {insuranceOptions.length > 0 && (
        <div className="travel-affiliate-group">
          <div className="travel-affiliate-group-title">
            🛡️ Travel insurance
          </div>
          <div className="travel-affiliate-grid">
            {insuranceOptions.map(opt => (
              <a key={opt.key} href={opt.url} target="_blank" rel="noopener noreferrer"
                className="affiliate-btn affiliate-food travel-affiliate-link"
                onClick={() => affiliateApi.track(opt.key, "trips", opt.url).catch(() => {})}
              >
                <span>{opt.icon}</span><span>{opt.label}</span>
                <span className="travel-affiliate-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {gearOptions.length > 0 && (
        <div className="travel-affiliate-group">
          <div className="travel-affiliate-group-title">
            🎒 Gear
          </div>
          <div className="travel-affiliate-grid">
            {gearOptions.map(opt => (
              <a key={opt.key} href={opt.url} target="_blank" rel="noopener noreferrer"
                className="affiliate-btn affiliate-food travel-affiliate-link"
                onClick={() => affiliateApi.track(opt.key, "trips", opt.url).catch(() => {})}
              >
                <span>{opt.icon}</span><span>{opt.label}</span>
                <span className="travel-affiliate-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <p className="affiliate-disclosure travel-affiliate-disclosure">
        Some links may be affiliate links. HouseMind may earn a small commission at no extra cost to you.
      </p>
    </div>
  );
}

function TemplatePackingPanel({
  data,
  loading,
  busy,
  customItem,
  onCustomItemChange,
  onGenerate,
  onToggle,
  onAddCustom,
}) {
  const [expanded, setExpanded] = useState(false);
  const categories = data?.categories || [];
  const totalItems = data?.total_items || 0;
  const checkedItems = data?.checked_items || 0;
  const progress = totalItems ? Math.round((checkedItems / totalItems) * 100) : 0;

  return (
    <div className="card mb-16 trip-template-packing-card is-compact">
      <div className="travel-tab-content-head" style={{ marginBottom: 10 }}>
        <div>
          <div className="travel-overview-section-label">✦ Starter checklist</div>
          <h3 style={{ margin: 0, fontSize: 20 }}>🧳 Packing checklist</h3>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
            Quick rules-based list for essentials and manual add-ons.
          </p>
        </div>
        <button type="button" className="travel-refresh-btn" onClick={onGenerate} disabled={busy || loading}>
          {busy ? "Building..." : totalItems ? "Refresh" : "Build"}
        </button>
      </div>

      {loading ? (
        <div className="trip-template-packing-loading">
          <span className="spinner" />
          Loading checklist...
        </div>
      ) : totalItems > 0 ? (
        <>
          <div className="travel-progress-card trip-template-packing-progress">
            <div className="travel-progress-head">
              <strong>{checkedItems}/{totalItems}</strong>
              <span>{progress}% packed</span>
            </div>
            <div className="travel-progress-bar"><span style={{ width: `${progress}%` }} /></div>
          </div>

          <div className="trip-template-packing-summary">
            <span>{categories.length} categories</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(value => !value)}>
              {expanded ? "Hide checklist" : "Review checklist"}
            </button>
          </div>

          {expanded && (
            <>
              <div className="trip-template-packing-list">
                {categories.map((category) => (
                  <div className="travel-packing-category" key={category.category}>
                    <div className="travel-packing-header">
                      <span>{getPackingCategoryIcon(category.category)}</span>
                      <strong>{category.category}</strong>
                      <em>{(category.items || []).filter((item) => item.is_checked).length} of {(category.items || []).length} packed</em>
                    </div>
                    <div className="travel-packing-items">
                      {(category.items || []).map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`travel-packing-item trip-template-packing-item${item.is_checked ? " is-checked" : ""}`}
                          onClick={() => onToggle(item)}
                          disabled={busy}
                        >
                          <span className="trip-template-packing-check" aria-hidden="true">
                            {item.is_checked ? "✓" : ""}
                          </span>
                          <span className="trip-template-packing-copy">
                            <strong>{item.item_name}</strong>
                            {item.quantity ? <em>{item.quantity}</em> : null}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <form className="trip-template-packing-add" onSubmit={onAddCustom}>
                <input
                  value={customItem}
                  onChange={(event) => onCustomItemChange(event.target.value)}
                  placeholder="Add one more thing..."
                  disabled={busy}
                />
                <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !customItem.trim()}>
                  Add
                </button>
              </form>
            </>
          )}
        </>
      ) : (
        <div className="trip-template-packing-empty">
          <p>No checklist yet. Build a quick starter list, then use AI only if you want richer trip-specific packing advice.</p>
        </div>
      )}
    </div>
  );
}

function TravelDocumentCard({ data, loading, onRefresh, onOpenVault }) {
  const items = data?.items || [];
  const docs = data?.documents || [];
  const required = data?.required_documents || [];
  const statusLabel = {
    urgent: "Action needed",
    needs_review: "Review",
    watch: "Watch",
    ready: "Ready",
  }[data?.status] || "Check";

  return (
    <div className={`card mb-16 trip-document-card is-${data?.status || "ready"}`}>
      <div className="travel-tab-content-head" style={{ marginBottom: 10 }}>
        <div>
          <div className="travel-overview-section-label">✦ Travel docs</div>
          <h3 style={{ margin: 0, fontSize: 20 }}>🛂 Passport & ID readiness</h3>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
            Checked against Home Vault dates and static destination rules.
          </p>
        </div>
        <span className={`trip-document-status is-${data?.status || "ready"}`}>{statusLabel}</span>
      </div>

      {loading ? (
        <div className="trip-template-packing-loading">
          <span className="spinner" />
          Checking travel documents...
        </div>
      ) : (
        <>
          <div className="trip-document-requirements">
            {(required.length ? required : ["Government ID"]).map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>

          {items.length > 0 ? (
            <div className="trip-document-alerts">
              {items.map((item) => (
                <div key={item.id} className={`trip-document-alert is-${item.priority}`}>
                  <strong>{item.title}</strong>
                  <p>{item.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="trip-document-ready">
              No document issues found from saved Home Vault records.
            </div>
          )}

          {docs.length > 0 && (
            <div className="trip-document-saved">
              {docs.slice(0, 4).map((doc) => (
                <span key={doc.id}>
                  {doc.name}{doc.expiry_date ? ` · expires ${new Date(`${doc.expiry_date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}` : " · expiry missing"}
                </span>
              ))}
            </div>
          )}

          <div className="trip-document-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenVault}>
              Open Home Vault
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onRefresh}>
              Refresh check
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function formatFlightTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function FlightStatusCard({ data, loading, tripDetails, onRefresh, onFlightChange }) {
  const flightNumber = tripDetails.flightNumber || data?.flight_number || "";
  const hasFlightNumber = Boolean(String(flightNumber || "").trim());
  const hasCheckedStatus = Boolean(data);
  const statusLabel = {
    needs_details: "Add flight",
    provider_not_configured: "Manual",
    unavailable: "Unavailable",
    not_found: "Not found",
    route_needs_review: "Confirm route",
    scheduled: "Scheduled",
    active: "Active",
    landed: "Landed",
    cancelled: "Cancelled",
  }[data?.status] || (hasFlightNumber ? "Not checked" : "Add flight");
  const departure = data?.departure || {};
  const arrival = data?.arrival || {};
  const checkedLabel = data?.checked_at ? formatFlightTime(data.checked_at) : "";

  return (
    <div className={`card mb-16 trip-flight-card is-${data?.status || "needs_details"}`}>
      <div className="travel-tab-content-head" style={{ marginBottom: 10 }}>
        <div>
          <div className="travel-overview-section-label">✦ Flight status</div>
          <h3 style={{ margin: 0, fontSize: 20 }}>{hasFlightNumber ? flightNumber : "Add flight number"}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
            Manual live check only. No AI request.
          </p>
        </div>
        <span className="badge" style={{ background: "var(--green-light)", color: "var(--green)" }}>
          {statusLabel}
        </span>
      </div>

      {loading ? (
        <div className="trip-template-packing-loading">
          <span className="spinner" />
          Checking flight...
        </div>
      ) : (
        <>
          <div className="travel-details-row travel-details-row--split" style={{ marginBottom: 10 }}>
            <div className="travel-details-field">
              <label>Flight number</label>
              <input
                className="form-input"
                value={tripDetails.flightNumber}
                onChange={(event) => onFlightChange?.("flightNumber", event.target.value.toUpperCase())}
                placeholder="DL123, UA456..."
              />
            </div>
            <div className="travel-details-field">
              <label>Airline</label>
              <input
                className="form-input"
                value={tripDetails.airline}
                onChange={(event) => onFlightChange?.("airline", event.target.value)}
                placeholder="Delta, United..."
              />
            </div>
          </div>
          <div className="travel-progress-card" style={{ display: "grid", gap: 8 }}>
            <div className="flex-between" style={{ gap: 10 }}>
              <strong>{data?.airline || tripDetails.airline || "Airline not set"}</strong>
              <span style={{ color: "var(--ink-muted)", fontSize: 13 }}>{data?.route || "Route pending"}</span>
            </div>
            <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 14 }}>
              {data?.message || (hasFlightNumber
                ? "Tap Check flight status when you want a live update. HouseMind will not auto-check."
                : "Book a flight first, then add the flight number here to check live status.")}
            </p>
            {hasCheckedStatus && checkedLabel ? (
              <span style={{ color: "var(--ink-muted)", fontSize: 12, fontWeight: 800 }}>
                {data.cache_hit ? "Cached result" : "Last checked"} · {checkedLabel}
              </span>
            ) : null}
          </div>

          {(departure.iata || arrival.iata || departure.scheduled || arrival.scheduled) && (
            <div className="trip-document-saved" style={{ marginTop: 10 }}>
              <span>{departure.iata || "Departure"}{departure.gate ? ` · gate ${departure.gate}` : ""}{departure.scheduled ? ` · ${formatFlightTime(departure.estimated || departure.scheduled)}` : ""}</span>
              <span>{arrival.iata || "Arrival"}{arrival.gate ? ` · gate ${arrival.gate}` : ""}{arrival.scheduled ? ` · ${formatFlightTime(arrival.estimated || arrival.scheduled)}` : ""}</span>
            </div>
          )}

          <div className="trip-document-actions">
            {data?.manual_check_url ? (
              <a className="btn btn-secondary btn-sm" href={data.manual_check_url} target="_blank" rel="noreferrer">
                Manual check
              </a>
            ) : null}
            <button type="button" className="btn btn-primary btn-sm" onClick={onRefresh} disabled={!hasFlightNumber}>
              {hasCheckedStatus ? "Refresh status" : "Check flight status"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ActivityCard({ activity, isSaved = false, onToggleSave = null, disabled = false }) {
  const categoryColors = {
    adventure: ["#EAF3EE", "#4A7C59"],
    culture: ["#EEF2FF", "#6366F1"],
    food: ["#FDEEE9", "#E7614A"],
    relaxation: ["#FDF6E3", "#C9A84C"],
    nightlife: ["#FDF2F8", "#C026D3"],
  };
  const [bg, fg] = categoryColors[activity.category] || ["var(--cream)", "var(--ink-muted)"];

  return (
    <div className="card card-sm travel-activity-card travel-activity-card--compact">
      <div className="travel-activity-top">
        <span className="badge" style={{ background: bg, color: fg }}>
          {activity.category}
        </span>
        <div className="travel-activity-actions">
          {activity.estimated_cost && (
            <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>{activity.estimated_cost}</span>
          )}
          {onToggleSave ? (
            <button
              type="button"
              className={`btn ${isSaved ? "btn-secondary" : "btn-ghost"} btn-sm`}
              onClick={onToggleSave}
              disabled={disabled}
            >
              {isSaved ? "✓ Saved" : "Save →"}
            </button>
          ) : null}
        </div>
      </div>
      <div className="travel-activity-title">
        {activity.name}
      </div>
      <p className="travel-activity-desc">
        {activity.description}
      </p>
      {activity.why_it_fits && (
        <p className="travel-activity-fit">
          ✦ {activity.why_it_fits}
        </p>
      )}
      <div className="travel-activity-meta">
        {activity.area && <span>📍 {activity.area}</span>}
        {activity.best_time && <span>⏰ {activity.best_time}</span>}
        {activity.booking_priority && <span>{activity.booking_priority}</span>}
      </div>
      {activity.tip && <div className="travel-activity-tip">💡 {activity.tip}</div>}
      {activity.backup_plan && (
        <div className="travel-activity-backup">🔄 Backup: {activity.backup_plan}</div>
      )}
    </div>
  );
}

function FoodCard({ place, isSaved = false, onToggleSave = null, disabled = false }) {
  return (
    <div className="card card-sm travel-food-card">
      <div className="flex-between mb-4">
        <span style={{ fontWeight: 500, fontSize: 15 }}>{place.name}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>{place.price_range}</span>
          {onToggleSave ? (
            <button
              type="button"
              className={`btn ${isSaved ? "btn-secondary" : "btn-ghost"} btn-sm`}
              onClick={() => onToggleSave(place.name)}
              disabled={disabled}
            >
              {isSaved ? "Saved to trip" : "Save to trip"}
            </button>
          ) : null}
        </div>
      </div>
      <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.5 }}>{place.description}</p>
      {place.why_it_fits && (
        <p style={{ fontSize: 13, color: "var(--green)", lineHeight: 1.45, marginTop: 8 }}>{place.why_it_fits}</p>
      )}
      <div style={{ display: "flex", gap: 10, fontSize: 12, color: "var(--ink-hint)", flexWrap: "wrap" }}>
        {place.area && <span>{place.area}</span>}
        {place.reservation_needed && <span>Reservation: {place.reservation_needed}</span>}
      </div>
    </div>
  );
}

function formatTripMoney(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(String(value).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(numeric) || numeric <= 0) return String(value);
  return `$${numeric.toLocaleString()}`;
}

function formatTripRange(entry) {
  if (entry?.low && entry?.high) return `${formatTripMoney(entry.low)}-${formatTripMoney(entry.high)}`;
  return formatTripMoney(entry?.amount || entry?.mid || 0);
}

function parseBudgetNumber(value) {
  const numeric = Number(String(value || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function buildBudgetBreakdown(tripDetails, activitiesData) {
  const suppliedBudget = parseBudgetNumber(tripDetails.budget);
  const textRange = activitiesData?.budget_summary?.range || "";
  const rangeNumbers = String(textRange).match(/\$?[\d,]+(?:\.\d+)?/g)?.map(parseBudgetNumber).filter(Boolean) || [];
  const total = suppliedBudget || rangeNumbers[Math.floor(rangeNumbers.length / 2)] || rangeNumbers[rangeNumbers.length - 1] || 950;
  const style = `${tripDetails.tripStyle || ""} ${tripDetails.transportMode || ""}`.toLowerCase();
  const hasFlights = !style.includes("road") && !style.includes("drive") && !style.includes("car");
  const entries = [
    { key: "flights", label: "Flights", amount: hasFlights ? total * 0.3 : total * 0.08, color: "#4C7BC5" },
    { key: "accommodation", label: "Accommodation", amount: total * 0.34, color: "#7C6AD7" },
    { key: "food", label: "Food & dining", amount: total * 0.2, color: "#38A878" },
    { key: "activities", label: "Activities", amount: total * 0.1, color: "#F4A340" },
    { key: "transport", label: hasFlights ? "Local transport" : "Car / fuel", amount: hasFlights ? total * 0.08 : total * 0.18, color: "#E2735A" },
    { key: "misc", label: "Misc", amount: total * 0.06, color: "#8792A2" },
  ];
  const rounded = entries.map(entry => ({ ...entry, amount: Math.max(20, Math.round(entry.amount / 10) * 10) }));
  const sum = rounded.reduce((acc, entry) => acc + entry.amount, 0) || total;
  return {
    total: Math.round(total),
    entries: rounded.map(entry => ({ ...entry, percent: Math.max(4, Math.round((entry.amount / sum) * 100)) })),
  };
}

function formatBudgetPerDay(activitiesData, total, duration) {
  const days = Math.max(1, Number(duration) || 1);
  const rangeNumbers = String(activitiesData?.budget_summary?.range || "").match(/\$?[\d,]+(?:\.\d+)?/g)?.map(parseBudgetNumber).filter(Boolean) || [];
  if (rangeNumbers.length >= 2) {
    const low = Math.max(10, Math.round((rangeNumbers[0] / days) / 10) * 10);
    const high = Math.max(low, Math.round((rangeNumbers[rangeNumbers.length - 1] / days) / 10) * 10);
    return `est. ${formatTripMoney(low)}-${formatTripMoney(high)}/day`;
  }
  const perDay = Math.max(10, Math.round((Number(total || 0) / days) / 10) * 10);
  return `est. ${formatTripMoney(perDay)}/day`;
}

function TripOverviewAction({ icon, title, detail, cta, tone = "", onClick }) {
  return (
    <button type="button" className={`trip-overview-action${tone ? ` is-${tone}` : ""}`} onClick={onClick}>
      <span>{icon}</span>
      <strong>{title}</strong>
      <em>{detail}</em>
      <small>{cta}</small>
    </button>
  );
}

function travelerInitial(name = "") {
  return String(name || "?").trim().charAt(0).toUpperCase() || "?";
}

function travelerLabel(travelers = [], fallback = "") {
  const names = travelers.map(item => item.name || item.name_override).filter(Boolean);
  if (names.length === 0) return fallback || "Just me";
  if (names.length === 1) return names[0];
  return `${names.length} travelers`;
}

function travelTravelerAvatar(traveler) {
  if (traveler.photo_url) return <img src={traveler.photo_url} alt="" />;
  return travelerInitial(traveler.name || traveler.name_override);
}

function TravelersSheet({ people, selected, onChange, onClose }) {
  const [manualName, setManualName] = useState("");
  const selectedPeople = new Set(selected.filter(item => item.person_id).map(item => Number(item.person_id)));
  const selectedNames = new Set(selected.filter(item => !item.person_id).map(item => String(item.name_override || item.name || "").toLowerCase()));

  const togglePerson = (person) => {
    onChange(selectedPeople.has(Number(person.id))
      ? selected.filter(item => Number(item.person_id) !== Number(person.id))
      : [...selected.filter(item => String(item.name_override || "").toLowerCase() !== "just me"), { person_id: person.id, name: person.name, relationship: person.relationship, photo_url: person.photo_url }]
    );
  };

  const chooseJustMe = () => onChange([{ name_override: "Just me", name: "Just me" }]);
  const addManual = () => {
    const name = manualName.trim();
    if (!name || selectedNames.has(name.toLowerCase())) return;
    onChange([...selected, { name_override: name, name }]);
    setManualName("");
  };

  return (
    <BottomSheet title="Travelers" label="Travelers" onClose={onClose} bodyClassName="travelers-sheet-body">
      <p className="travelers-sheet-subtitle">Select people HouseMind should plan for.</p>
        <button type="button" className={`traveler-row${selectedNames.has("just me") ? " is-selected" : ""}`} onClick={chooseJustMe}>
          <span className="traveler-avatar">J</span>
          <strong>Just me</strong>
          <em>Solo trip</em>
          <small>{selectedNames.has("just me") ? "✓" : ""}</small>
        </button>
        <div className="travelers-list">
          {people.map(person => {
            const selectedPerson = selectedPeople.has(Number(person.id));
            return (
              <button type="button" key={person.id} className={`traveler-row${selectedPerson ? " is-selected" : ""}`} onClick={() => togglePerson(person)}>
                <span className="traveler-avatar">
                  {person.photo_url ? <img src={person.photo_url} alt="" /> : travelerInitial(person.name)}
                </span>
                <strong>{person.name}</strong>
                <em>{person.relationship}</em>
                <small>{selectedPerson ? "✓" : ""}</small>
              </button>
            );
          })}
        </div>
        <div className="traveler-manual-row">
          <input value={manualName} onChange={event => setManualName(event.target.value)} placeholder="+ Add by name" />
          <button type="button" onClick={addManual}>Add</button>
        </div>
    </BottomSheet>
  );
}

function TripAskBar({ tripDetails, eventType, eventId, activeTab }) {
  const prompts = ["What should I do next?", "What am I missing?", "Use pantry before I leave?"];
  const hasTripContext = Boolean(
    tripDetails?.destination ||
    tripDetails?.title ||
    tripDetails?.startDate ||
    tripDetails?.tripStyle
  );

  function openTripAsk(question = "") {
    const clean = String(question || "").trim();
    if (!hasTripContext) {
      window.dispatchEvent(new CustomEvent("hm_open_ask", { detail: { question: clean || "Ask HouseMind." } }));
      return;
    }

    const contextParts = [
      tripDetails.destination ? `Destination: ${tripDetails.destination}` : null,
      tripDetails.startDate ? `Dates: ${tripDetails.startDate}${tripDetails.endDate ? ` to ${tripDetails.endDate}` : ""}` : null,
      tripDetails.duration ? `Duration: ${tripDetails.duration} days` : null,
      tripDetails.tripStyle ? `Style: ${tripDetails.tripStyle}` : null,
      tripDetails.travelers ? `Travelers: ${tripDetails.travelers}` : null,
      tripDetails.budget ? `Budget: ${tripDetails.budget}` : null,
      tripDetails.transportMode ? `Transport: ${tripDetails.transportMode}` : null,
      tripDetails.dietary ? `Food needs: ${tripDetails.dietary}` : null,
    ].filter(Boolean).join(" | ");
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: clean,
        context: {
          module: "events",
          module_label: "Events & Trips",
          surface: `Trip ${activeTab || "planner"}`,
          event_type: eventType,
          event_id: eventId,
          destination: tripDetails.destination || "",
          trip_style: tripDetails.tripStyle || "",
          summary: contextParts,
          dates: tripDetails.startDate ? `${tripDetails.startDate}${tripDetails.endDate ? ` to ${tripDetails.endDate}` : ""}` : "",
          duration: tripDetails.duration || "",
          travelers: tripDetails.travelers || "",
          budget: tripDetails.budget || "",
          transport_mode: tripDetails.transportMode || "",
          dietary: tripDetails.dietary || "",
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-trip-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder={hasTripContext ? "✦ Ask about this trip..." : "✦ Ask HouseMind..."}
        onFocus={() => openTripAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button
            key={prompt}
            type="button"
            className="fd-food-askbar__chip"
            onClick={() => openTripAsk(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openTripAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

export default function TripPage() {
  const { eventType, eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, applyAiUsage } = useAuth();
  const toast = useToast();

  const fallbackDestination = searchParams.get("destination") || "";
  const fallbackTripStyle = searchParams.get("style") || "";
  const fallbackDuration = parseInt(searchParams.get("duration") || "7");
  const fallbackTravelers = searchParams.get("travelers") || "";

  const initialTab = ["details", "packing", "plan", "overview"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "details";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [tripDetails, setTripDetails] = useState({
    title: fallbackDestination || "Your trip",
    contextLabel: fallbackTravelers || "",
    destination: fallbackDestination,
    tripStyle: fallbackTripStyle,
    duration: fallbackDuration,
    travelers: fallbackTravelers,
    notes: "",
    startDate: "",
    endDate: "",
    ...emptyTripContext,
  });
  const [packingData, setPackingData] = useState(null);
  const [templatePacking, setTemplatePacking] = useState(null);
  const [templatePackingLoading, setTemplatePackingLoading] = useState(false);
  const [templatePackingBusy, setTemplatePackingBusy] = useState(false);
  const [templatePackingCustomItem, setTemplatePackingCustomItem] = useState("");
  const [travelDocuments, setTravelDocuments] = useState(null);
  const [travelDocumentsLoading, setTravelDocumentsLoading] = useState(false);
  const [flightStatus, setFlightStatus] = useState(null);
  const [flightStatusLoading, setFlightStatusLoading] = useState(false);
  const [activitiesData, setActivitiesData] = useState(null);
  const [savedPlanMeta, setSavedPlanMeta] = useState(null);
  const [moneyDraft, setMoneyDraft] = useState({ planned_budget: "", actual_spend: "" });
  const [tripPlanLimit, setTripPlanLimit] = useState(null);
  const [travelerProfile, setTravelerProfile] = useState(null);
  const [people, setPeople] = useState([]);
  const [tripTravelers, setTripTravelers] = useState([]);
  const [travelersSheetOpen, setTravelersSheetOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [editingTripDetails, setEditingTripDetails] = useState(true);
  const [error, setError] = useState(null);
  const [editingPacking, setEditingPacking] = useState(false);
  const [editingDayPlan, setEditingDayPlan] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [refreshOverlayMessage, setRefreshOverlayMessage] = useState("");
  const [detailSaveMessage, setDetailSaveMessage] = useState("");
  const [pantryExpiring, setPantryExpiring] = useState([]);
  const [packingDraft, setPackingDraft] = useState({
    dont_forget: [],
    packing_list: [],
    pro_tips: [],
    saved_items: [],
  });
  const [dayPlanDraft, setDayPlanDraft] = useState([]);
  const [provider, setProvider] = useState(user?.ai_provider || "openai");
  const remainingRequests = Math.max((user?.ai_requests_limit ?? 0) - (user?.ai_requests_used ?? 0), 0);
  const currentData = activeTab === "packing" ? packingData : activeTab === "plan" ? activitiesData : null;
  const generationMode = activeTab === "packing" ? "packing" : "plan";
  const summaryTitle = tripDetails.title || tripDetails.destination || "Your trip";
  const compactTripTitle = summaryTitle.replace(/\s+trip$/i, "");
  const tripDateLabel = tripDetails.startDate && tripDetails.endDate
    ? `${new Date(`${tripDetails.startDate}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}-${new Date(`${tripDetails.endDate}T00:00:00`).toLocaleDateString(undefined, { day: "numeric" })}`
    : `${tripDetails.duration || 7} days`;
  const dayPlanCount = activitiesData?.day_by_day?.length || 0;
  const packingCategoryCount = packingData?.packing_list?.length || 0;
  const hasSavedPlan = Boolean(packingData || activitiesData);
  const planLockedByFreeLimit = Boolean(tripPlanLimit?.reached && !hasSavedPlan);
  const savedPackingItems = normalizeSavedPackingItems(packingData?.saved_items || []);
  const savedPackingKeys = savedPackingItems.map((item) => `${item.category || ""}::${item.item}`.toLowerCase());
  const packingItemCount = (packingData?.packing_list || []).reduce((sum, cat) => sum + (cat.items?.length || 0), 0);
  const packingProgress = packingItemCount ? Math.round((savedPackingItems.length / packingItemCount) * 100) : 0;
  const travelerHighlights = travelerProfile?.highlights?.slice(0, 5) || [];
  const savedActivityItems = (activitiesData?.shortlist?.activities || []).map((item) => (
    typeof item === "string" ? { name: item } : item
  ));
  const savedFoodItems = (activitiesData?.shortlist?.food_and_drink || []).map((item) => (
    typeof item === "string" ? { name: item } : item
  ));
  const savedActivityNames = savedActivityItems.map((item) => item.name);
  const savedFoodNames = savedFoodItems.map((item) => item.name);
  const shortlistedActivities = savedActivityItems;
  const shortlistedFood = savedFoodItems;
  const tripStart = parseLocalDate(tripDetails.startDate);
  const todayForTrip = new Date();
  todayForTrip.setHours(0, 0, 0, 0);
  const daysUntilTrip = tripStart ? daysBetween(todayForTrip, tripStart) : null;
  const hasPlanBudget = Boolean(activitiesData?.budget_summary);
  const overviewBudget = buildBudgetBreakdown(tripDetails, activitiesData);
  const planBudgetSummary = hasPlanBudget
    ? `Budget: ${formatTripMoney(overviewBudget.total)} · ${formatBudgetPerDay(activitiesData, overviewBudget.total, tripDetails.duration)}`
    : "";
  const planBudgetBrief = activitiesData?.trip_brief?.budget_snapshot || planBudgetSummary;
  const unpackedItems = (packingData?.packing_list || [])
    .flatMap(category => (category.items || []).map(item => ({ ...item, category: category.category })))
    .filter(item => !savedPackingKeys.includes(`${item.category || ""}::${item.item}`.toLowerCase()) && !savedPackingKeys.includes(`::${item.item}`.toLowerCase()));
  const tripPrepItems = buildTripPrepItems({ tripDetails, pantryExpiring, travelDocuments, templatePacking });
  const beforeYouLeaveCount = tripPrepItems.length;

  const token = localStorage.getItem("hm_access_token");

  const loadTemplatePacking = useCallback(async () => {
    if (!token || !["packing", "overview"].includes(activeTab)) return;
    setTemplatePackingLoading(true);
    try {
      const data = await tripGet(`/trips/packing-items/${eventType}/${eventId}`, token);
      setTemplatePacking(data);
    } catch {
      setTemplatePacking(null);
    } finally {
      setTemplatePackingLoading(false);
    }
  }, [activeTab, eventId, eventType, token]);

  const openTripPrepTarget = (target) => {
    if (target === "packing") {
      setActiveTab("packing");
    } else if (target === "details") {
      setActiveTab("details");
      setEditingTripDetails(true);
    } else if (target === "food") {
      navigate("/app/food");
    } else if (target === "vault") {
      navigate("/app/home-vault");
    } else if (target === "vehicles") {
      navigate("/app/vehicles");
    } else if (target === "subscriptions") {
      navigate("/app/subscriptions");
    }
  };

  const loadTravelDocuments = useCallback(async () => {
    if (!token || !["packing", "overview"].includes(activeTab)) return;
    setTravelDocumentsLoading(true);
    try {
      const data = await tripGet(`/trips/travel-documents/${eventType}/${eventId}`, token);
      setTravelDocuments(data);
    } catch {
      setTravelDocuments(null);
    } finally {
      setTravelDocumentsLoading(false);
    }
  }, [activeTab, eventId, eventType, token]);

  const loadFlightStatus = useCallback(async () => {
    if (!token || !["details", "overview"].includes(activeTab)) return;
    if (!String(tripDetails.flightNumber || "").trim()) return;
    setFlightStatusLoading(true);
    try {
      const data = await tripRequest(`/trips/flight-status/${eventType}/${eventId}`, {
        flight_number: tripDetails.flightNumber,
        airline: tripDetails.airline,
        origin: tripDetails.origin,
        destination: tripDetails.destination,
        start_date: tripDetails.startDate,
        notes: composeTripNotes(tripDetails.notes, tripDetails),
      }, token);
      setFlightStatus(data);
      if (data?.notification_created) {
        window.dispatchEvent(new Event("hm_notifications_refresh"));
      }
    } catch {
      setFlightStatus(null);
    } finally {
      setFlightStatusLoading(false);
    }
  }, [activeTab, eventId, eventType, token, tripDetails]);

  const updateFlightDetail = useCallback((key, value) => {
    setFlightStatus(null);
    setTripDetails((current) => ({ ...current, [key]: value }));
  }, []);

  useEffect(() => {
    peopleApi.listCompact().then(setPeople).catch(() => setPeople([]));
  }, []);

  useEffect(() => {
    loadTemplatePacking();
  }, [loadTemplatePacking]);

  useEffect(() => {
    loadTravelDocuments();
  }, [loadTravelDocuments]);

  useEffect(() => {
    setPackingDraft({
      dont_forget: packingData?.dont_forget || [],
      packing_list: (packingData?.packing_list || []).map((category) => ({
        category: category.category || "",
        items: (category.items || []).map((item) => ({
          item: item.item || "",
          quantity: item.quantity || "",
          note: item.note || "",
          why: item.why || "",
        })),
      })),
      pro_tips: packingData?.pro_tips || [],
      saved_items: normalizeSavedPackingItems(packingData?.saved_items || []),
    });
  }, [packingData]);

  // Cross-module: fetch pantry items expiring before or during the trip window.
  useEffect(() => {
    if (!tripDetails.destination && !tripDetails.startDate) return;
    pantryApi.list().then(items => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = parseLocalDate(tripDetails.startDate) || today;
      const end = parseLocalDate(tripDetails.endDate);
      const cutoff = end || new Date(start);
      if (!end) {
        cutoff.setDate(cutoff.getDate() + Number(tripDetails.duration || 7) + 3);
      }
      const expiring = (items || []).filter(item => {
        if (!item.expiry_date) return false;
        const exp = new Date(item.expiry_date + "T00:00:00");
        return exp >= today && exp <= cutoff;
      });
      setPantryExpiring(expiring);
    }).catch(() => {});
  }, [tripDetails.destination, tripDetails.duration, tripDetails.endDate, tripDetails.startDate]);

  useEffect(() => {
    setDayPlanDraft(buildDayPlanDraft(activitiesData?.day_by_day || []));
  }, [activitiesData]);

  useEffect(() => {
    let ignore = false;
    if (!token) {
      setLoadingPlan(false);
      return;
    }
    setLoadingPlan(true);
    tripGet(`/trips/${eventType}/${eventId}`, token)
      .then((data) => {
        if (ignore) return;
        const trip = data.trip || {};
        const savedPlan = data.saved_plan || {};
        const parsedNotes = splitTripNotes(trip.notes || "");
        setTripDetails({
          title: trip.title || fallbackDestination || "Your trip",
          contextLabel: trip.context_label || fallbackTravelers || "",
          destination: trip.destination || fallbackDestination,
          tripStyle: trip.trip_style || fallbackTripStyle,
          duration: trip.duration || fallbackDuration,
          travelers: trip.travelers || fallbackTravelers,
          notes: parsedNotes.notes,
          startDate: trip.start_date || "",
          endDate: trip.end_date || "",
          ...parsedNotes.context,
        });
        setProvider(trip.provider || savedPlan.provider || user?.ai_provider || "openai");
        setPackingData(savedPlan.packing_data || null);
        setActivitiesData(savedPlan.activities_data || null);
        setSavedPlanMeta(savedPlan || null);
        setMoneyDraft({
          planned_budget: savedPlan.planned_budget ?? "",
          actual_spend: savedPlan.actual_spend ?? "",
        });
        setEditingTripDetails(false);
        setTripPlanLimit(data.trip_plan_limit || null);
        setTravelerProfile(data.traveler_profile || null);
        setTripTravelers(data.trip_travelers || []);
      })
      .catch(() => {
        if (ignore) return;
        setTripDetails({
          title: fallbackDestination || "Your trip",
          contextLabel: fallbackTravelers || "",
          destination: fallbackDestination,
          tripStyle: fallbackTripStyle,
          duration: fallbackDuration,
          travelers: fallbackTravelers,
          notes: "",
          startDate: "",
          endDate: "",
          ...emptyTripContext,
        });
        setProvider(user?.ai_provider || "openai");
        setSavedPlanMeta(null);
        setTripPlanLimit(null);
        setTravelerProfile(null);
        setTripTravelers([]);
      })
      .finally(() => {
        if (!ignore) setLoadingPlan(false);
      });
    return () => {
      ignore = true;
    };
  }, [eventId, eventType, fallbackDestination, fallbackDuration, fallbackTravelers, fallbackTripStyle, token, user?.ai_provider]);

  const requestConfirm = ({ title, message, onConfirm }) => setConfirmState({ title, message, onConfirm });
  const closeConfirm = () => setConfirmState(null);
  const runConfirmedAction = async (cb) => {
    closeConfirm();
    await cb();
  };

  const buildTripDetailPayload = useCallback(() => ({
    destination: tripDetails.destination,
    trip_style: tripDetails.tripStyle,
    duration: tripDetails.duration,
    travelers: tripDetails.travelers,
    trip_travelers: tripTravelers.map(item => item.person_id ? { person_id: item.person_id } : { name_override: item.name_override || item.name }),
    notes: composeTripNotes(tripDetails.notes, tripDetails),
    provider,
  }), [provider, tripDetails, tripTravelers]);

  const updateTripTravelers = (nextTravelers) => {
    setTripTravelers(nextTravelers);
    setTripDetails(current => ({
      ...current,
      travelers: travelerLabel(nextTravelers, current.travelers),
      contextLabel: travelerLabel(nextTravelers, current.contextLabel),
    }));
  };

  const persistTripDetails = async ({ quiet = false } = {}) => {
    if (!quiet) {
      setSavingEdits(true);
      setError(null);
      setDetailSaveMessage("Saving trip details...");
    }
    const body = buildTripDetailPayload();
    try {
      if (eventType === "group") {
        await apiPatch(`/groups/events/${eventId}`, {
          destination: body.destination,
          trip_style: body.trip_style,
          notes: body.notes,
        }, token);
      } else if (eventType === "event") {
        await apiPatch(`/events/personal/${eventId}`, {
          destination: body.destination,
          trip_style: body.trip_style,
          notes: body.notes,
          end_date: tripDetails.endDate || null,
        }, token);
      } else {
        await apiPatch(`/occasions/${eventId}`, {
          notes: body.notes,
        }, token);
      }

      if (savedPlanMeta?.id) {
        const data = await tripPatch(`/trips/${eventType}/${eventId}`, {
          destination: body.destination,
          trip_style: body.trip_style,
          duration: body.duration,
          travelers: body.travelers,
          notes: body.notes,
          provider,
        }, token);
        setSavedPlanMeta(data.saved_plan || null);
        setTravelerProfile(data.traveler_profile || null);
        setTripTravelers(data.trip_travelers || tripTravelers);
      }

      if (!quiet) {
        setEditingTripDetails(false);
        toast?.("Trip details saved", "success");
      }
      return body;
    } catch (e) {
      if (!quiet) {
        setError(e.message);
        toast?.(e.message || "Could not save trip details", "error");
      }
      throw e;
    } finally {
      if (!quiet) {
        setSavingEdits(false);
        window.setTimeout(() => setDetailSaveMessage(""), 700);
      }
    }
  };

  const checkFlightStatus = async () => {
    if (!String(tripDetails.flightNumber || "").trim()) return;
    await persistTripDetails({ quiet: true });
    await loadFlightStatus();
  };

  const generateTemplatePacking = async () => {
    setTemplatePackingBusy(true);
    setError(null);
    try {
      const body = await persistTripDetails({ quiet: true });
      const data = await tripRequest(`/trips/packing-items/${eventType}/${eventId}/generate`, {
        ...body,
        start_date: tripDetails.startDate,
        end_date: tripDetails.endDate,
      }, token);
      setTemplatePacking(data);
      toast?.(data.created_count > 0 ? "Packing checklist built" : "Checklist already up to date", "success");
    } catch (e) {
      setError(e.message || "Could not build the free packing checklist");
      toast?.("Could not build checklist", "error");
    } finally {
      setTemplatePackingBusy(false);
    }
  };

  const toggleTemplatePackingItem = async (item) => {
    if (!item?.id) return;
    setTemplatePackingBusy(true);
    try {
      const updated = await tripPatch(`/trips/packing-items/item/${item.id}`, {
        is_checked: !item.is_checked,
      }, token);
      setTemplatePacking((current) => {
        if (!current?.categories) return current;
        const categories = current.categories.map((category) => ({
          ...category,
          items: (category.items || []).map((entry) => entry.id === updated.id ? updated : entry),
        }));
        const checkedItems = categories.reduce((sum, category) => (
          sum + (category.items || []).filter((entry) => entry.is_checked).length
        ), 0);
        return { ...current, categories, checked_items: checkedItems };
      });
    } catch (e) {
      setError(e.message || "Could not update checklist item");
      toast?.("Could not update checklist", "error");
    } finally {
      setTemplatePackingBusy(false);
    }
  };

  const addTemplatePackingItem = async (event) => {
    event.preventDefault();
    const itemName = templatePackingCustomItem.trim();
    if (!itemName) return;
    setTemplatePackingBusy(true);
    try {
      await tripRequest(`/trips/packing-items/${eventType}/${eventId}`, {
        item_name: itemName,
        category: "Added",
      }, token);
      setTemplatePackingCustomItem("");
      await loadTemplatePacking();
    } catch (e) {
      setError(e.message || "Could not add checklist item");
      toast?.("Could not add checklist item", "error");
    } finally {
      setTemplatePackingBusy(false);
    }
  };

  const refreshTravelDocuments = async () => {
    setTravelDocumentsLoading(true);
    setError(null);
    try {
      const body = buildTripDetailPayload();
      const data = await tripRequest(`/trips/travel-documents/${eventType}/${eventId}`, {
        ...body,
        start_date: tripDetails.startDate,
        end_date: tripDetails.endDate,
      }, token);
      setTravelDocuments(data);
    } catch (e) {
      setError(e.message || "Could not refresh travel document check");
      toast?.("Could not refresh travel document check", "error");
    } finally {
      setTravelDocumentsLoading(false);
    }
  };

  const runGenerate = async () => {
    setLoading(true);
    setError(null);
    setRefreshOverlayMessage(generationMode === "packing" ? "Building packing list..." : "Refreshing trip brief...");
    try {
      const body = await persistTripDetails({ quiet: true });
      if (generationMode === "packing") {
        const data = await tripRequest(
          `/trips/packing/${eventType}/${eventId}`, body, token
        );
        setPackingData(data);
        setSavedPlanMeta(data.saved_plan || null);
        setTripPlanLimit(data.trip_plan_limit || { reached: false, can_save: true });
        setTravelerProfile(data.traveler_profile || null);
        applyAiUsage(data.ai_usage);
      } else {
        const data = await tripRequest(
          `/trips/activities/${eventType}/${eventId}`, body, token
        );
        setActivitiesData(data);
        setSavedPlanMeta(data.saved_plan || null);
        setTripPlanLimit(data.trip_plan_limit || { reached: false, can_save: true });
        setTravelerProfile(data.traveler_profile || null);
        applyAiUsage(data.ai_usage);
      }
      setTripDetails((current) => ({
        ...current,
        destination: body.destination,
        tripStyle: body.trip_style,
        duration: body.duration,
        travelers: body.travelers,
        notes: tripDetails.notes,
      }));
      toast?.(generationMode === "packing" ? "Packing list refreshed" : "Trip brief refreshed", "success");
    } catch (e) {
      if (e.data?.ai_usage) applyAiUsage(e.data.ai_usage);
      if (e.data?.trip_plan_limit_reached) {
        setTripPlanLimit(e.data.trip_plan_limit || null);
        setError("Free plan includes one saved trip plan. You can keep using your existing saved plan, or upgrade to create another.");
      } else {
        setError(e.data?.upgrade_required
          ? "You've used your free AI suggestions. Upgrade to keep getting personalized ideas and planning tools."
          : e.message
        );
      }
      toast?.("Could not refresh this trip right now", "error");
    } finally {
      setLoading(false);
      setRefreshOverlayMessage("");
    }
  };

  const handleGenerate = async () => {
    if (!currentData) {
      requestConfirm({
        title: generationMode === "packing" ? "Generate AI packing advice?" : "Generate AI trip plan?",
        message: generationMode === "packing"
          ? "This uses 1 AI request. Use the starter checklist first for essentials; generate AI packing only when you want richer destination-specific advice."
          : "This uses 1 AI request. Generate when you want a richer day plan, activities, food picks, and budget guidance.",
        onConfirm: runGenerate,
      });
      return;
    }

    requestConfirm({
      title: generationMode === "packing" ? "Refresh packing list" : "Refresh trip brief",
      message: generationMode === "packing"
        ? "This uses 1 AI request. Saved packing-list edits and saved packing items will stay. Unsaved items from the current AI version may be replaced."
        : "This uses 1 AI request. Saved trip picks will stay. Activities and food spots you have not saved may be replaced.",
      onConfirm: runGenerate,
    });
  };

  const handleSaveActivityEdits = async (nextActivitiesData) => {
    setSavingEdits(true);
    setError(null);
    try {
      const data = await tripPatch(`/trips/${eventType}/${eventId}`, {
        activities_data: nextActivitiesData,
      }, token);
      setActivitiesData(data.saved_plan?.activities_data || nextActivitiesData);
      setSavedPlanMeta(data.saved_plan || null);
      setTravelerProfile(data.traveler_profile || null);
      toast?.("Trip edits saved", "success");
      return data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSavingEdits(false);
    }
  };

  const handleSavePackingEdits = async (nextPackingData) => {
    const payload = {
      ...(nextPackingData || {}),
      saved_items: normalizeSavedPackingItems(nextPackingData?.saved_items || []),
    };
    setSavingEdits(true);
    setError(null);
    try {
      const data = await tripPatch(`/trips/${eventType}/${eventId}`, {
        packing_data: payload,
      }, token);
      setPackingData(data.saved_plan?.packing_data || payload);
      setSavedPlanMeta(data.saved_plan || null);
      setTravelerProfile(data.traveler_profile || null);
      toast?.("Packing list saved", "success");
      return data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSavingEdits(false);
    }
  };

  const savePackingEdits = async () => {
    const nextPackingData = {
      ...(packingData || {}),
      dont_forget: (packingDraft.dont_forget || []).map((item) => item.trim()).filter(Boolean),
      packing_list: (packingDraft.packing_list || []).map((category) => ({
        category: category.category.trim(),
        items: (category.items || [])
          .map((item) => ({
            item: (item.item || "").trim(),
            quantity: Number(item.quantity) || undefined,
            note: (item.note || "").trim(),
            why: (item.why || "").trim(),
          }))
          .filter((item) => item.item),
      })).filter((category) => category.category && category.items.length > 0),
      pro_tips: (packingDraft.pro_tips || []).map((item) => item.trim()).filter(Boolean),
      saved_items: normalizeSavedPackingItems(packingData?.saved_items || []),
    };
    await handleSavePackingEdits(nextPackingData);
    setEditingPacking(false);
  };

  const openDayPlanEditor = () => {
    setDayPlanDraft(buildDayPlanDraft(activitiesData?.day_by_day || []));
    setEditingDayPlan(true);
  };

  const cancelDayPlanEditor = () => {
    setDayPlanDraft(buildDayPlanDraft(activitiesData?.day_by_day || []));
    setEditingDayPlan(false);
  };

  const saveDayPlanEdits = async () => {
    const nextActivitiesData = {
      ...(activitiesData || {}),
      day_by_day: dayPlanDraft.map((day, index) => ({
        day: day.day || index + 1,
        theme: day.theme.trim(),
        pace: day.pace || "",
        highlights: day.highlights.split("\n").map((item) => item.trim()).filter(Boolean),
        notes: day.notes || "",
      })),
    };
    await handleSaveActivityEdits(nextActivitiesData);
    setEditingDayPlan(false);
  };

  const toggleShortlistItem = async (kind, item) => {
    const current = activitiesData?.shortlist || { activities: [], food_and_drink: [] };
    const currentValues = current[kind] || [];
    const nextValues = currentValues.some((entry) => entry.name === item.name)
      ? currentValues.filter((entry) => entry.name !== item.name)
      : [...currentValues, item];

    const nextActivitiesData = {
      ...(activitiesData || {}),
      shortlist: {
        activities: kind === "activities" ? nextValues : (current.activities || []),
        food_and_drink: kind === "food_and_drink" ? nextValues : (current.food_and_drink || []),
      },
    };

    await handleSaveActivityEdits(nextActivitiesData);
  };

  const toggleSavedPackingItem = async (item) => {
    const normalizedItem = normalizePackingItem(item);
    if (!normalizedItem) return;

    const currentItems = normalizeSavedPackingItems(packingData?.saved_items || []);
    const nextItems = currentItems.some((entry) => samePackingItem(entry, normalizedItem))
      ? currentItems.filter((entry) => !samePackingItem(entry, normalizedItem))
      : [...currentItems, normalizedItem];

    const nextPackingData = {
      ...(packingData || {}),
      saved_items: nextItems,
    };

    await handleSavePackingEdits(nextPackingData);
  };

  const saveMoneyTracking = async () => {
    setSavingEdits(true);
    setError(null);
    try {
      const data = await tripPatch(`/trips/${eventType}/${eventId}`, moneyDraft, token);
      setSavedPlanMeta(data.saved_plan || null);
      setMoneyDraft({
        planned_budget: data.saved_plan?.planned_budget ?? "",
        actual_spend: data.saved_plan?.actual_spend ?? "",
      });
      toast?.("Trip spending saved to Household Budget", "success");
    } catch (err) {
      setError(err.message || "Failed to save trip spending.");
      toast?.(err.message || "Failed to save trip spending.", "error");
    } finally {
      setSavingEdits(false);
    }
  };

  return (
    <div className="travel-page hm-desktop-page-shell">
      <div className="flex-between mb-16">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate("/app/events")}>← Events & Trips</button>
        <div className="flex-center gap-8">
          <span className="text-sm text-muted">AI:</span>
          <select className="form-select" style={{ width: "auto", padding: "6px 12px", fontSize: 13 }}
            value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="anthropic">Claude (Anthropic)</option>
            <option value="openai">OpenAI (gpt-4o-mini)</option>
          </select>
        </div>
      </div>

      <div className="travel-hero">
        <div className="travel-hero-copy">
          <div className="travel-hero-eyebrow">Travel planner</div>
          <h2>
            {compactTripTitle} <span>trip</span>
          </h2>
          <p>{tripDateLabel} · {tripDetails.duration || 7} days · {tripDetails.travelers || tripDetails.contextLabel || "Traveler"}</p>
          <div className="travel-pill-row">
            <DetailPill label="🏖️" value={tripDetails.tripStyle || "Flexible"} />
            <DetailPill label="⏱️" value={tripDetails.pace || "Balanced"} />
            <DetailPill label="💰" value={tripDetails.budget || "Budget set"} />
            <DetailPill label="🚗" value={tripDetails.transportMode || "Transport"} />
          </div>
        </div>
        <div className="travel-hero-side">
          <TravelStatCard
            label="AI requests remaining"
            value={`${remainingRequests} left`}
            hint={user?.reset_date ? `Resets ${new Date(`${user.reset_date}T00:00:00`).toLocaleDateString()}` : "Resets next month"}
          />
          <TravelStatCard
            label="Packing sections"
            value={packingCategoryCount || "Not generated"}
          />
          <TravelStatCard
            label="Planned days"
            value={dayPlanCount || "Not generated"}
          />
        </div>
      </div>

      <div className="travel-toolbar travel-toolbar--primary">
        <div className="travel-tabs travel-tabs--mobile">
          {[
            { key: "details", icon: "✏️", label: "Details", sub: "Trip fields" },
            { key: "packing", icon: "🧳", label: "Packing", sub: "Checklist" },
            { key: "plan", icon: "🗺", label: "Plan", sub: "Days and activities" },
            { key: "overview", icon: "📋", label: "Overview", sub: "Brief and prep" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`travel-tab-btn${activeTab === tab.key ? " is-active" : ""}`}>
              <span>{tab.icon}</span>
              <small>{tab.label}</small>
            </button>
          ))}
        </div>

      </div>

      {activeTab === "overview" && (
        <div className="trip-overview-dashboard">
          <div className="trip-overview-header">
            <div>
              <div className="travel-overview-section-label">{summaryTitle} · Overview</div>
              <h3>{tripDateLabel}</h3>
            </div>
          </div>

          <div className="trip-overview-stat-grid">
            <TravelStatCard
              label="Days until trip"
              value={daysUntilTrip === null ? "Set date" : daysUntilTrip < 0 ? "Past" : daysUntilTrip}
              hint={tripDetails.startDate || "No start date"}
            />
            <TravelStatCard
              label="Packing"
              value={packingItemCount ? `${savedPackingItems.length}/${packingItemCount}` : "0/0"}
              hint={`${packingProgress}% complete`}
            />
            <TravelStatCard
              label="Days planned"
              value={`${dayPlanCount || 0}/${tripDetails.duration || 0}`}
              hint={dayPlanCount ? "itinerary set" : "needs plan"}
            />
            <TravelStatCard
              label="Budget"
              value={hasPlanBudget ? formatTripMoney(overviewBudget.total) : "Plan needed"}
              hint={activitiesData?.budget_summary?.range || "generate trip plan"}
            />
          </div>

          <section className="trip-budget-breakdown">
            <div className="travel-overview-section-label">💰 Budget breakdown</div>
            {hasPlanBudget ? (
              <>
                <div className="trip-budget-lines">
                  {overviewBudget.entries.map(entry => (
                    <div key={entry.key} className="trip-budget-line">
                      <span>{entry.label}</span>
                      <strong>{formatTripRange(entry)}</strong>
                    </div>
                  ))}
                </div>
                <div className="trip-budget-colorbar">
                  {overviewBudget.entries.map(entry => (
                    <i key={entry.key} style={{ width: `${entry.percent}%`, background: entry.color }} />
                  ))}
                </div>
              </>
            ) : (
              <div className="trip-budget-empty">
                <p>Generate your trip plan to see the budget breakdown.</p>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setActiveTab("plan")}>
                  Go to Plan
                </button>
              </div>
            )}
          </section>

          <section className="hm-source-money-card">
            <div>
              <strong>Money tracking</strong>
              <span>Keep the estimate while planning; add actual spend when the trip is underway or complete.</span>
            </div>
            <div className="hm-source-money-grid">
              <label><span>Planned budget</span><div className="hm-money-input"><i>$</i><input type="number" min="0" step="0.01" inputMode="decimal" value={moneyDraft.planned_budget} onChange={(e) => setMoneyDraft((current) => ({ ...current, planned_budget: e.target.value }))} placeholder="0.00" /></div></label>
              <label><span>Actual spent</span><div className="hm-money-input"><i>$</i><input type="number" min="0" step="0.01" inputMode="decimal" value={moneyDraft.actual_spend} onChange={(e) => setMoneyDraft((current) => ({ ...current, actual_spend: e.target.value }))} placeholder="0.00" /></div></label>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={saveMoneyTracking} disabled={savingEdits}>{savingEdits ? "Saving..." : "Save spending"}</button>
          </section>

          <div className="trip-overview-action-grid">
            <TripOverviewAction
              icon="🧳"
              title={`${unpackedItems.length} item${unpackedItems.length !== 1 ? "s" : ""} still to pack`}
              detail={unpackedItems.slice(0, 3).map(item => item.item).join(" · ") || "Packing list ready"}
              cta="Finish packing"
              tone="amber"
              onClick={() => setActiveTab("packing")}
            />
            <TripOverviewAction
              icon="🏠"
              title="Before you leave"
              detail={`${beforeYouLeaveCount} checklist item${beforeYouLeaveCount !== 1 ? "s" : ""} to review`}
              cta="Review"
              tone="rose"
              onClick={() => document.querySelector(".trip-prep-card")?.scrollIntoView({ behavior: "smooth", block: "center" })}
            />
            {pantryExpiring.length > 0 ? (
              <TripOverviewAction
                icon="🥗"
                title={`Use up ${pantryExpiring.length} pantry item${pantryExpiring.length !== 1 ? "s" : ""}`}
                detail={pantryExpiring.slice(0, 3).map(item => item.name).join(" · ")}
                cta="Food"
                tone="green"
                onClick={() => document.querySelector(".trip-prep-card")?.scrollIntoView({ behavior: "smooth", block: "center" })}
              />
            ) : null}
          </div>
          <div className="trip-overview-updated">
            Last updated: {savedPlanMeta?.updated_at ? new Date(savedPlanMeta.updated_at).toLocaleDateString() : "Not refreshed yet"}
          </div>
        </div>
      )}

      {activeTab === "details" && (
        <>
        <div className="travel-tab-content-head travel-tab-content-head--card">
          <div>
            <h3>📝 Trip details</h3>
            <p>{editingTripDetails ? "AI uses these details for packing and plan precision." : "Saved. Edit before refreshing AI if plans need to change."}</p>
          </div>
          <button
            className="travel-refresh-btn"
            onClick={() => editingTripDetails ? persistTripDetails() : setEditingTripDetails(true)}
            disabled={savingEdits}
          >
            {savingEdits ? "Saving..." : editingTripDetails ? "Save" : "Edit"}
          </button>
        </div>

        <fieldset className={`travel-details-card${editingTripDetails ? "" : " is-readonly"}`} disabled={!editingTripDetails}>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Destination</label>
              <input
                className="form-input"
                value={tripDetails.destination}
                onChange={(e) => setTripDetails((current) => ({ ...current, destination: e.target.value }))}
                placeholder="Paris, Maui, Nashville..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Leaving from</label>
              <input
                className="form-input"
                value={tripDetails.origin}
                onChange={(e) => setTripDetails((current) => ({ ...current, origin: e.target.value }))}
                placeholder="Atlanta, JFK, home city..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Staying near</label>
              <input
                className="form-input"
                value={tripDetails.lodgingArea}
                onChange={(e) => setTripDetails((current) => ({ ...current, lodgingArea: e.target.value }))}
                placeholder="Downtown, resort area, near family..."
              />
            </div>
          </div>
          <div className="travel-details-row travel-details-row--split">
            <div className="travel-details-field">
              <label>Duration</label>
              <input
                type="number"
                min="1"
                className="form-input"
                value={tripDetails.duration}
                onChange={(e) => setTripDetails((current) => ({ ...current, duration: Math.max(1, Number(e.target.value) || 1) }))}
              />
            </div>
            <div className="travel-details-field">
              <label>Travelers</label>
              <button
                type="button"
                className="travelers-picker-field"
                onClick={() => setTravelersSheetOpen(true)}
              >
                {tripTravelers.length > 0 ? (
                  <span className="travelers-chip-row">
                    {tripTravelers.slice(0, 4).map((traveler, index) => (
                      <span key={traveler.id || traveler.person_id || traveler.name_override || index} className="traveler-chip">
                        <span>{travelTravelerAvatar(traveler)}</span>
                        {traveler.name || traveler.name_override}
                      </span>
                    ))}
                    {tripTravelers.length > 4 ? <span className="traveler-chip">+{tripTravelers.length - 4}</span> : null}
                  </span>
                ) : (
                  <span className="travelers-placeholder">Choose from People or add by name</span>
                )}
              </button>
            </div>
          </div>
          <div className="travel-details-row travel-details-row--split">
            <div className="travel-details-field">
              <label>Budget</label>
              <input
                className="form-input"
                value={tripDetails.budget}
                onChange={(e) => setTripDetails((current) => ({ ...current, budget: e.target.value }))}
                placeholder="$1,500 total, mid-range, low cost..."
              />
            </div>
            <div className="travel-details-field">
              <label>Trip style</label>
              <input
                className="form-input"
                value={tripDetails.tripStyle}
                onChange={(e) => setTripDetails((current) => ({ ...current, tripStyle: e.target.value }))}
                placeholder="Beach, city break, family reunion..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Transportation</label>
              <input
                className="form-input"
                value={tripDetails.transportMode}
                onChange={(e) => setTripDetails((current) => ({ ...current, transportMode: e.target.value }))}
                placeholder="Rental car, walking, rideshare, train..."
              />
            </div>
          </div>
          <div className="travel-details-row travel-details-row--split">
            <div className="travel-details-field">
              <label>Flight number</label>
              <input
                className="form-input"
                value={tripDetails.flightNumber}
                onChange={(e) => setTripDetails((current) => ({ ...current, flightNumber: e.target.value.toUpperCase() }))}
                placeholder="DL123, UA456..."
              />
            </div>
            <div className="travel-details-field">
              <label>Airline</label>
              <input
                className="form-input"
                value={tripDetails.airline}
                onChange={(e) => setTripDetails((current) => ({ ...current, airline: e.target.value }))}
                placeholder="Delta, United, Southwest..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Pace</label>
              <input
                className="form-input"
                value={tripDetails.pace}
                onChange={(e) => setTripDetails((current) => ({ ...current, pace: e.target.value }))}
                placeholder="Slow mornings, packed days, balanced..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Notes</label>
              <textarea
                className="form-textarea"
                rows={2}
                value={tripDetails.notes}
                onChange={(e) => setTripDetails((current) => ({ ...current, notes: e.target.value }))}
                placeholder="Kid-friendly, want slower mornings, prioritize food spots..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Food needs</label>
              <input
                className="form-input"
                value={tripDetails.dietary}
                onChange={(e) => setTripDetails((current) => ({ ...current, dietary: e.target.value }))}
                placeholder="Vegetarian, allergies, picky eaters..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Mobility or accessibility</label>
              <input
                className="form-input"
                value={tripDetails.mobility}
                onChange={(e) => setTripDetails((current) => ({ ...current, mobility: e.target.value }))}
                placeholder="Low walking, stroller-friendly, wheelchair access, older traveler..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Must do</label>
              <textarea
                className="form-textarea"
                rows={2}
                value={tripDetails.mustDo}
                onChange={(e) => setTripDetails((current) => ({ ...current, mustDo: e.target.value }))}
                placeholder="Beach sunset, museum, local food, birthday dinner..."
              />
            </div>
          </div>
          <div className="travel-details-row">
            <div className="travel-details-field">
              <label>Avoid</label>
              <textarea
                className="form-textarea"
                rows={2}
                value={tripDetails.avoid}
                onChange={(e) => setTripDetails((current) => ({ ...current, avoid: e.target.value }))}
                placeholder="Long hikes, late nights, tourist traps, spicy food..."
              />
            </div>
          </div>
          {hasSavedPlan && (
            <div className="travel-details-saved">
              Saved to this trip.
            </div>
          )}
        </fieldset>
        <FlightStatusCard
          data={flightStatus}
          loading={flightStatusLoading}
          tripDetails={tripDetails}
          onRefresh={checkFlightStatus}
          onFlightChange={updateFlightDetail}
        />
        </>
      )}

      {activeTab === "overview" && travelerProfile && (
        <div className="travel-overview-block">
          <div className="travel-overview-section-label">✦ Planned for this trip</div>
          <div className="card travel-overview-personalized">
            <div className="travel-overview-ai-label">+ HouseMind personalized</div>
            <div className="travel-overview-main-copy">
              HouseMind is using {tripDetails.contextLabel || "the selected person"}'s profile to personalize this trip.
              {travelerProfile.summary ? ` ${travelerProfile.summary}` : ""}
            </div>
            {travelerHighlights.length > 0 && (
              <div className="travel-overview-chip-row">
                {travelerHighlights.map((item) => (
                  <span key={item} className="travel-overview-chip">
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "overview" && (
        <>
          <TravelDocumentCard
            data={travelDocuments}
            loading={travelDocumentsLoading}
            onRefresh={refreshTravelDocuments}
            onOpenVault={() => navigate("/app/home-vault")}
          />
          <FlightStatusCard
            data={flightStatus}
            loading={flightStatusLoading}
            tripDetails={tripDetails}
            onRefresh={checkFlightStatus}
            onFlightChange={updateFlightDetail}
          />
          <TripPrepCard tripDetails={tripDetails} prepItems={tripPrepItems} onOpenTarget={openTripPrepTarget} />
        </>
      )}

      {planLockedByFreeLimit && (
        <div className="card mb-16" style={{ borderColor: "var(--gold)", background: "var(--gold-light)" }}>
          <p style={{ margin: 0, color: "#8B6914", fontSize: 14 }}>
            Free plan includes one saved trip plan. You can still view this trip screen, but creating another saved plan needs Pro.
          </p>
        </div>
      )}

      {error && (
        <div className="card mb-16 travel-error-card" style={{ borderColor: "var(--rose)" }}>
          <p style={{ color: "var(--rose-dark)", fontSize: 14 }}>{error}</p>
        </div>
      )}

      {loadingPlan && (
        <div style={{ textAlign: "center", padding: "18px 0", color: "var(--ink-muted)" }}>
          <span className="spinner" style={{ marginRight: 8 }} />
          Loading saved trip plan…
        </div>
      )}

      {activeTab === "packing" && !loadingPlan && (
        <>
          <TravelDocumentCard
            data={travelDocuments}
            loading={travelDocumentsLoading}
            onRefresh={refreshTravelDocuments}
            onOpenVault={() => navigate("/app/home-vault")}
          />
          <TemplatePackingPanel
            data={templatePacking}
            loading={templatePackingLoading}
            busy={templatePackingBusy}
            customItem={templatePackingCustomItem}
            onCustomItemChange={setTemplatePackingCustomItem}
            onGenerate={generateTemplatePacking}
            onToggle={toggleTemplatePackingItem}
            onAddCustom={addTemplatePackingItem}
          />
        </>
      )}

      {/* Cross-module: pantry items expiring before you return */}
      {activeTab === "packing" && packingData && pantryExpiring.length > 0 && (
        <div className="card mb-16" style={{
          borderColor: "var(--gold)", background: "var(--gold-light)",
          display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>🥗</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#8B6914", marginBottom: 4 }}>
              Use before you go · {pantryExpiring.length} item{pantryExpiring.length !== 1 ? "s" : ""} expiring while you're away
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {pantryExpiring.slice(0, 6).map(item => (
                <span key={item.id} className="badge" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>
                  {item.name}{item.expiry_date ? ` · ${new Date(item.expiry_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </span>
              ))}
              {pantryExpiring.length > 6 && (
                <span className="badge" style={{ background: "#FEF3C7", color: "#92400E" }}>+{pantryExpiring.length - 6} more</span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => navigate("/app/food")}
              style={{ fontSize: 12, color: "#92400E", padding: "4px 0", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              View pantry →
            </button>
          </div>
        </div>
      )}

      {activeTab === "packing" && packingData && (
        <div>
          <div className="travel-tab-content-head travel-tab-content-head--card">
            <div>
              <h3>🧳 Packing list</h3>
              <p>{tripDetails.destination || summaryTitle} · {tripDetails.duration || 7} days · {tripDetails.tripStyle || "Flexible"}</p>
            </div>
            <button className="travel-refresh-btn" onClick={handleGenerate} disabled={loading || planLockedByFreeLimit}>
              ✦ Refresh
            </button>
          </div>

          <div className="travel-progress-card">
            <div className="travel-progress-head">
              <strong>{savedPackingItems.length}/{packingItemCount}</strong>
              <span>{savedPackingItems.length} of {packingItemCount} packed · {packingProgress}%</span>
            </div>
            <div className="travel-progress-bar"><span style={{ width: `${packingProgress}%` }} /></div>
          </div>

          {(packingData.packing_strategy || packingData.trip_specific_warnings?.length > 0) && (
            <TravelSection
              eyebrow="Packing logic"
              title="Why this list fits"
            >
              <div className="suggestions-grid">
                {packingData.packing_strategy && (
                  <div className="card card-sm">
                    <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55, margin: 0 }}>
                      {packingData.packing_strategy}
                    </p>
                  </div>
                )}
                {packingData.trip_specific_warnings?.length > 0 && (
                  <div className="card card-sm">
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {packingData.trip_specific_warnings.map((item) => (
                        <div key={item} style={{ fontSize: 14, color: "var(--ink)" }}>{item}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TravelSection>
          )}

          {savedPackingItems.length > 0 && (
            <TravelSection
              eyebrow="Saved items"
              title="Saved packing"
            >
              <div className="suggestions-grid">
                <div className="card card-sm">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {savedPackingItems.map((item, index) => (
                      <div key={`${item.category || "General"}-${item.item}-${index}`} style={{ fontSize: 14, color: "var(--ink)" }}>
                        <strong>{item.category || "General"}:</strong> {item.quantity && item.quantity > 1 ? `${item.item} x${item.quantity}` : item.item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TravelSection>
          )}

          {packingData.dont_forget?.length > 0 && (
            <div className="travel-packing-priority">
              <div className="travel-overview-section-label">⚠️ Don’t forget</div>
              <div className="travel-overview-chip-row">
                {packingData.dont_forget.map((item, i) => (
                  <span key={i} className="travel-overview-chip travel-overview-chip--urgent">{item}</span>
                ))}
              </div>
            </div>
          )}

          <TravelSection
            eyebrow="🧳 Checklist"
            title="Packing list"
          >
            <div className="flex-between mb-16" style={{ gap: 8 }}>
              <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
                {editingPacking ? "Edit categories or notes, then save." : ""}
              </div>
              <div className="flex-center gap-8">
                {editingPacking ? (
                  <>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingPacking(false)} disabled={savingEdits}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={savePackingEdits} disabled={savingEdits}>
                      {savingEdits ? "Saving..." : "Save packing list"}
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingPacking(true)}>
                    Edit packing list
                  </button>
                )}
              </div>
            </div>
            {(editingPacking ? packingDraft.packing_list : packingData.packing_list)?.map((cat, i) => (
              editingPacking ? (
                <EditablePackingCategory
                  key={i}
                  category={cat.category}
                  items={cat.items}
                  onCategoryChange={(value) => setPackingDraft((current) => ({
                    ...current,
                    packing_list: current.packing_list.map((item, index) => index === i ? { ...item, category: value } : item),
                  }))}
                  onItemChange={(itemIndex, field, value) => setPackingDraft((current) => ({
                    ...current,
                    packing_list: current.packing_list.map((item, index) => index === i ? {
                      ...item,
                      items: item.items.map((entry, entryIndex) => entryIndex === itemIndex ? { ...entry, [field]: value } : entry),
                    } : item),
                  }))}
                />
              ) : (
                <PackingCategory
                  key={i}
                  category={cat.category}
                  items={cat.items}
                  savedItemKeys={savedPackingKeys}
                  onToggleSave={toggleSavedPackingItem}
                  disabled={savingEdits}
                  tripStyle={tripDetails.tripStyle || ""}
                />
              )
            ))}
          </TravelSection>

          {packingData.pro_tips?.length > 0 && (
            <div className="card mt-24" style={{ background: "var(--gold-light)", borderColor: "var(--gold)" }}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: "#8B6914" }}>💡 Helpful travel notes</div>
              <ul style={{ paddingLeft: 16, margin: 0 }}>
                {packingData.pro_tips.map((tip, i) => (
                  <li key={i} style={{ fontSize: 14, color: "#8B6914", marginBottom: 4 }}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === "plan" && activitiesData && (
        <div>
          <div className="travel-tab-content-head travel-tab-content-head--card">
            <div>
              <h3>🗺️ Trip plan</h3>
              <p>{tripDetails.destination || summaryTitle} · {tripDetails.duration || 7} days · {tripDetails.tripStyle || "Flexible"}</p>
            </div>
            <button className="travel-refresh-btn" onClick={handleGenerate} disabled={loading || planLockedByFreeLimit}>
              ✦ Refresh
            </button>
          </div>

          {planBudgetBrief ? (
            <button type="button" className="trip-plan-budget-brief" onClick={() => setActiveTab("overview")}>
              <span>Budget brief</span>
              <strong>{planBudgetSummary || "Budget from trip plan"}</strong>
              <em>{planBudgetBrief}</em>
            </button>
          ) : null}

          {activitiesData.day_by_day?.length > 0 && (
            <TravelSection
              eyebrow="📅 Day by day"
              title="Trip rhythm"
            >
              <div className="flex-between mb-16" style={{ gap: 8 }}>
                <div />
                <div className="flex-center gap-8">
                  <button type="button" className="travel-edit-plan-btn" onClick={openDayPlanEditor}>
                    ✏️ Edit day plan
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {activitiesData.day_by_day.map((day, i) => (
                  <div key={i} className="card card-sm travel-day-card" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                      background: "var(--rose-light)", color: "var(--rose)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "var(--font-display)", fontSize: 16,
                    }}>
                      {day.day}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{day.theme}</div>
                      <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
                        {day.highlights?.join(", ")}
                      </div>
                      {(day.pace || day.notes) && (
                        <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 6 }}>
                          {day.pace ? `${day.pace} pace` : ""}{day.pace && day.notes ? " · " : ""}{day.notes || ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TravelSection>
          )}

          {(shortlistedActivities.length > 0 || shortlistedFood.length > 0) && (
            <TravelSection
              eyebrow="⭐ Shortlist"
              title="Final picks to keep"
              subtitle="Save the ideas you actually want so the trip starts feeling decided."
            >
              <div className="suggestions-grid">
                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Saved activities
                  </div>
                  {shortlistedActivities.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {shortlistedActivities.map((item) => (
                        <div key={item.name} style={{ fontSize: 14, color: "var(--ink)" }}>{item.name}</div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Saved food stops
                  </div>
                  {shortlistedFood.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {shortlistedFood.map((item) => (
                        <div key={item.name} style={{ fontSize: 14, color: "var(--ink)" }}>{item.name}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </TravelSection>
          )}

          {activitiesData.activities?.length > 0 && (
            <TravelSection
              eyebrow="🗺️ Activities"
              title="Things to do"
            >
              <div className="suggestions-grid">
                {activitiesData.activities.map((a, i) => (
                  <ActivityCard
                    key={i}
                    activity={a}
                    isSaved={savedActivityNames.includes(a.name)}
                    onToggleSave={() => toggleShortlistItem("activities", a)}
                    disabled={savingEdits}
                  />
                ))}
              </div>
            </TravelSection>
          )}

          {shortlistedFood.length > 0 && (
            <TravelSection
              eyebrow="🍽️ Shortlist"
              title="Saved food stops"
            >
              <div className="suggestions-grid">
                {shortlistedFood.map((item) => (
                  <div key={item.name} className="card card-sm" style={{ fontSize: 14, color: "var(--ink)" }}>
                    {item.name}
                  </div>
                ))}
              </div>
            </TravelSection>
          )}

          {activitiesData.food_and_drink?.length > 0 && (
            <TravelSection
              eyebrow="🍽️ Eat well"
              title="Food and drink"
            >
              <div className="suggestions-grid">
                {activitiesData.food_and_drink.map((f, i) => (
                  <FoodCard
                    key={i}
                    place={f}
                    isSaved={savedFoodNames.includes(f.name)}
                    onToggleSave={() => toggleShortlistItem("food_and_drink", f)}
                    disabled={savingEdits}
                  />
                ))}
              </div>
            </TravelSection>
          )}

          <TripAffiliateSection
            destination={tripDetails.destination}
            tripStyle={tripDetails.tripStyle}
            checkin={tripDetails.startDate}
            checkout={tripDetails.endDate}
            guests={tripDetails.travelers || 1}
          />
        </div>
      )}

      {editingDayPlan && (
        <div className="travel-plan-editor-backdrop" onClick={cancelDayPlanEditor}>
          <div className="travel-plan-editor-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="travel-plan-editor-handle" />
            <div className="travel-plan-editor-head">
              <div>
                <div className="travel-overview-section-label">📅 Edit day plan</div>
                <h3>⏱️ Trip rhythm</h3>
              </div>
              <button type="button" onClick={cancelDayPlanEditor} disabled={savingEdits} aria-label="Close day plan editor">
                ×
              </button>
            </div>
            <div className="travel-plan-editor-body">
              {dayPlanDraft.map((day, i) => (
                <div key={i} className="travel-plan-editor-day">
                  <div className="travel-plan-editor-number">{day.day || i + 1}</div>
                  <div className="travel-plan-editor-fields">
                    <label>
                      <span>Day theme</span>
                      <input
                        className="form-input"
                        value={day.theme}
                        onChange={(e) => setDayPlanDraft((current) => current.map((item, index) => index === i ? { ...item, theme: e.target.value } : item))}
                      />
                    </label>
                    <label>
                      <span>Highlights</span>
                      <textarea
                        className="form-textarea"
                        rows={5}
                        value={day.highlights}
                        onChange={(e) => setDayPlanDraft((current) => current.map((item, index) => index === i ? { ...item, highlights: e.target.value } : item))}
                        placeholder="One highlight per line"
                      />
                    </label>
                    <label>
                      <span>Notes</span>
                      <textarea
                        className="form-textarea"
                        rows={3}
                        value={day.notes}
                        onChange={(e) => setDayPlanDraft((current) => current.map((item, index) => index === i ? { ...item, notes: e.target.value } : item))}
                        placeholder="Optional pace, timing, or backup notes"
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="travel-plan-editor-actions">
              <button type="button" className="btn btn-secondary" onClick={cancelDayPlanEditor} disabled={savingEdits}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveDayPlanEdits} disabled={savingEdits}>
                {savingEdits ? "Saving..." : "Save day plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {(activeTab === "packing" || activeTab === "plan") && !currentData && !loading && (
        <div className="travel-empty-state">
          <div className="travel-empty-icon">{generationMode === "packing" ? "📦" : "🗺"}</div>
          <h3>{generationMode === "packing" ? "Want richer AI packing advice?" : "Start your trip brief"}</h3>
          <p>
            {generationMode === "packing"
              ? "Use the starter checklist for essentials. Generate the AI packing plan when you want deeper destination-specific notes."
              : "Generate a real trip brief with planning notes, activity ideas, food picks, and a simple day-by-day outline."}
          </p>
          <div className="travel-empty-actions">
            <button type="button" className="travel-refresh-btn" onClick={handleGenerate} disabled={loading || planLockedByFreeLimit}>
              ✦ {generationMode === "packing" ? "Generate packing" : "Generate plan"}
            </button>
            <button type="button" className="travel-edit-plan-btn" onClick={() => setActiveTab("details")}>
              ✏️ Edit details first
            </button>
          </div>
        </div>
      )}

      {confirmState && (
        <div className="app-modal-backdrop" onClick={closeConfirm}>
          <div
            className="app-modal-card admin-confirm-modal"
            style={{ maxWidth: 480, width: "calc(100% - 32px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="admin-confirm-title">{confirmState.title}</h3>
            <p className="admin-confirm-message">{confirmState.message}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirm}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => runConfirmedAction(confirmState.onConfirm)}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && refreshOverlayMessage && (
        <SavingNotice title="Refreshing" message={refreshOverlayMessage} />
      )}

      {detailSaveMessage && (
        <SavingNotice title="Saving" message={detailSaveMessage} />
      )}

      {!editingPacking && !editingDayPlan && !travelersSheetOpen && !confirmState && !refreshOverlayMessage && !detailSaveMessage ? (
        <TripAskBar
          tripDetails={tripDetails}
          eventType={eventType}
          eventId={eventId}
          activeTab={activeTab}
        />
      ) : null}

      {travelersSheetOpen && (
        <TravelersSheet
          people={people}
          selected={tripTravelers}
          onChange={updateTripTravelers}
          onClose={() => setTravelersSheetOpen(false)}
        />
      )}
    </div>
  );
}
